import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Logo } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Building2, Wrench, ArrowLeft, ArrowRight, Check, Loader2, GraduationCap, Home } from "lucide-react";

const SERVICES = ["CCTV & Security", "Waterproofing", "Painting", "Electrical", "Plumbing",
  "Pumps", "Solar", "Fire Safety", "STP", "Lift", "Pest Control", "Housekeeping"];

const EXPERT_FIELDS = ["Legal / Bye-laws", "Chartered Accountant", "Audit", "Structural Engineer",
  "Architect", "Taxation", "Insurance", "Society Management", "Vaastu", "Redevelopment"];

export default function Onboarding() {
  const { user, loading, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [path, setPath] = useState(null); // 'society' | 'vendor'
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ services_interested: [], categories: [], buildings: 1, state: "Maharashtra" });
  const [applicationChecked, setApplicationChecked] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (!loading && user && user.onboarded && user.role) {
      if (user.role === "vendor") navigate("/vendor");
      else if (user.role === "expert") navigate("/expert");
      else if (user.role === "society_member") navigate("/resident");
      else if (user.role === "super_admin" || user.role === "staff") navigate("/admin");
      else navigate("/app");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!loading && user && (!user.role || !user.onboarded) && !path) {
      try {
        const intent = sessionStorage.getItem("msm_onboard_type") || user.onboard_intent;
        if (intent === "society") { setPath("society"); setStep(1); }
        else if (intent === "vendor") {
          const applicantType = sessionStorage.getItem("msm_vendor_applicant_type");
          setPath("vendor");
          if (applicantType === "TECHNICIAN") {
            setForm((current) => ({ ...current, applicant_type: "TECHNICIAN" }));
          }
        }
        else if (intent === "expert") setPath("expert");
        else if (intent === "resident") setPath("resident");
        sessionStorage.removeItem("msm_onboard_type");
        sessionStorage.removeItem("msm_vendor_applicant_type");
      } catch {}
    }
  }, [user, loading, path]);

  useEffect(() => {
    if (loading || !user || user.onboarded || user.role || applicationChecked) {
      return;
    }
    let cancelled = false;
    api
      .get("/my-application")
      .then((response) => {
        if (cancelled) return;
        const active = (response.data || []).find(
          (application) => application.status !== "REJECTED",
        );
        if (active?.type === "vendor") {
          navigate("/vendor/pending", { replace: true });
          return;
        }
        if (active?.type === "expert") {
          navigate("/expert/pending", { replace: true });
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setApplicationChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [applicationChecked, loading, navigate, user]);

  const [resMatches, setResMatches] = useState(null);
  const [otpOk, setOtpOk] = useState({ email: false, mobile: false });
  const [currentLegal, setCurrentLegal] = useState(null);
  const [ackTerms, setAckTerms] = useState(false);
  const [ackPrivacy, setAckPrivacy] = useState(false);
  const [pilotMatch, setPilotMatch] = useState(null);
  const [inviteCheck, setInviteCheck] = useState({ status: "idle", pilot: null });

  useEffect(() => {
    if (path !== "vendor") return;
    const applicantType = form.applicant_type || "VENDOR";
    api
      .get(`/legal/current?applicant_type=${applicantType}`)
      .then((response) => setCurrentLegal(response.data))
      .catch(() => setCurrentLegal(null));
  }, [path, form.applicant_type]);

  useEffect(() => {
    // Look up a pilot that matches the applicant's city + first category.
    if (path !== "vendor" || form.applicant_type !== "TECHNICIAN") {
      setPilotMatch(null);
      return;
    }
    const city = (form.city || "").trim();
    const category = (form.categories || [])[0];
    if (!city || !category) { setPilotMatch(null); return; }
    api
      .get(`/pilots/match?city=${encodeURIComponent(city)}&category=${encodeURIComponent(category)}`)
      .then((response) => setPilotMatch(response.data.match))
      .catch(() => setPilotMatch(null));
  }, [path, form.applicant_type, form.city, (form.categories || []).join(",")]);

  const checkInvite = async () => {
    const code = (form.pilot_invite_code || "").trim();
    if (!code) { setInviteCheck({ status: "idle", pilot: null }); return; }
    try {
      const response = await api.get(`/pilots/invite/validate?code=${encodeURIComponent(code)}`);
      if (response.data.ok) setInviteCheck({ status: "ok", pilot: response.data.pilot });
      else setInviteCheck({ status: "bad", pilot: null });
    } catch (error) {
      setInviteCheck({ status: "bad", pilot: null });
    }
  };
  useEffect(() => {
    if (path === "resident" && resMatches === null) {
      api.get("/resident/lookup").then((r) => setResMatches(r.data.matches || [])).catch(() => setResMatches([]));
    }
  }, [path, resMatches]);

  const activateResident = async (member_id) => {
    setSaving(true);
    try {
      await api.post("/resident/activate", member_id ? { member_id } : {});
      await checkAuth();
      toast.success("Welcome to your resident portal!");
      navigate("/resident");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not activate");
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const submitSociety = async () => {
    if (!form.name) { toast.error("Society name is required"); setStep(1); return; }
    if (!otpOk.email) { toast.error("Please verify your email first"); setStep(4); return; }
    setSaving(true);
    try {
      const r = await api.post("/apply/society", {
        ...form,
        society_name: form.name,
        name: form.secretary_name || user.name,
        email: form.email || user.email,
        mobile: form.mobile || form.secretary_mobile,
      });
      await checkAuth();
      toast.success(`Society registered — ${r.data.display_id}. Your trial workspace is ready.`);
      navigate("/app");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not register society");
    } finally { setSaving(false); }
  };

  const submitVendor = async () => {
    if (!form.business_name) { toast.error("Business name is required"); return; }
    if (!otpOk.email) { toast.error("Please verify your email first"); return; }
    if (!currentLegal) { toast.error("Agreement is still loading, please wait"); return; }
    if (!(ackTerms && ackPrivacy)) { toast.error("Please tick both acknowledgements to continue"); return; }
    setSaving(true);
    try {
      const idempotency_key = `vendor-apply-${user.user_id}-${Date.now()}`;
      const r = await api.post("/apply/vendor", {
        ...form,
        email: form.email || user.email,
        applicant_type: form.applicant_type || "VENDOR",
        pilot_invite_code: form.pilot_invite_code || "",
        agreement_acceptance: {
          agreement_id: currentLegal.agreement.agreement_id,
          schedule_id: currentLegal.schedule.schedule_id,
          accepted_terms: true,
          accepted_privacy: true,
          idempotency_key,
        },
      });
      await checkAuth();
      toast.success(`Application submitted — ${r.data.display_id}`);
      navigate("/vendor/pending");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit application");
    } finally { setSaving(false); }
  };

  const submitExpert = async () => {
    const expertName = (form.name || user.name || "").trim();
    const specializations = form.categories || [];
    if (!expertName || specializations.length === 0) {
      toast.error("Name and at least one specialization are required");
      return;
    }
    if (!otpOk.email) { toast.error("Please verify your email first"); return; }
    setSaving(true);
    try {
      const r = await api.post("/apply/expert", {
        ...form,
        name: expertName,
        categories: specializations,
        email: form.email || user.email,
      });
      await checkAuth();
      toast.success(`Application submitted — ${r.data.display_id}. Our team will review it.`);
      navigate("/expert/pending");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit application");
    } finally { setSaving(false); }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;
  if (!user.onboarded && !user.role && !applicationChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="onboarding-status-checking">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          Checking your application status…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b bg-card"><div className="max-w-3xl mx-auto px-6 h-16 flex items-center"><Logo /></div></div>
      <div className="max-w-3xl mx-auto px-6 py-10">
        {!path && (
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Welcome, {user.name?.split(" ")[0]} 👋</h1>
            <p className="text-muted-foreground mt-2">How would you like to use MySocietyMitra?</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
              <button data-testid="onboard-resident-btn" onClick={() => setPath("resident")}
                className="text-left bg-card border rounded-xl p-6 hover:-translate-y-1 hover:shadow-lg transition-transform">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Home className="w-6 h-6" /></div>
                <h3 className="font-display font-bold text-lg mt-4">I'm a Resident</h3>
                <p className="text-sm text-muted-foreground mt-1">See your own bills, ledger and society notices.</p>
              </button>
              <button data-testid="onboard-society-btn" onClick={() => { setPath("society"); setStep(1); }}
                className="text-left bg-card border rounded-xl p-6 hover:-translate-y-1 hover:shadow-lg transition-transform">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Building2 className="w-6 h-6" /></div>
                <h3 className="font-display font-bold text-lg mt-4">Register My Society</h3>
                <p className="text-sm text-muted-foreground mt-1">Free society workspace with accounting, tools, documents & more.</p>
              </button>
              <button data-testid="onboard-vendor-btn" onClick={() => setPath("vendor")}
                className="text-left bg-card border rounded-xl p-6 hover:-translate-y-1 hover:shadow-lg transition-transform">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center"><Wrench className="w-6 h-6" /></div>
                <h3 className="font-display font-bold text-lg mt-4">Become a Vendor Partner</h3>
                <p className="text-sm text-muted-foreground mt-1">Receive RFQs, submit quotes and manage projects.</p>
              </button>
              <button data-testid="onboard-expert-btn" onClick={() => setPath("expert")}
                className="text-left bg-card border rounded-xl p-6 hover:-translate-y-1 hover:shadow-lg transition-transform">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center"><GraduationCap className="w-6 h-6" /></div>
                <h3 className="font-display font-bold text-lg mt-4">Join as an Expert</h3>
                <p className="text-sm text-muted-foreground mt-1">Offer paid consultations to societies and earn per minute.</p>
              </button>
            </div>
          </div>
        )}

        {path === "resident" && (
          <div className="bg-card border rounded-xl p-6 md:p-8">
            <h2 className="font-display text-xl font-bold">Resident Login</h2>
            <p className="text-sm text-muted-foreground mt-1">We match your flat using your email <b>{user.email}</b>.</p>
            {resMatches === null ? (
              <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : resMatches.length === 0 ? (
              <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="resident-no-match">
                We couldn't find a flat record linked to <b>{user.email}</b>. Please ask your society admin/secretary to add this email to your flat record, then try again.
              </div>
            ) : (
              <div className="mt-5 space-y-3">
                {resMatches.map((m) => (
                  <button key={m.member_id} onClick={() => activateResident(m.member_id)} disabled={saving}
                    data-testid={`resident-match-${m.member_id}`}
                    className="w-full text-left bg-background border rounded-xl p-4 hover:border-primary hover:shadow-md transition-all flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold">{m.society_name}</div>
                      <div className="text-xs text-muted-foreground">{m.name} · Flat {m.wing ? `${m.wing}-` : ""}{m.flat}</div>
                    </div>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4 text-primary" />}
                  </button>
                ))}
              </div>
            )}
            <Button variant="ghost" onClick={() => { setPath(null); setResMatches(null); }} className="gap-2 rounded-full mt-5"><ArrowLeft className="w-4 h-4" /> Back</Button>
          </div>
        )}

        {path === "society" && (
          <div className="bg-card border rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-2 mb-6">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-primary" : "bg-muted"}`} />
              ))}
            </div>
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="font-display text-xl font-bold">Society Details</h2>
                <Field label="Society Name *"><Input data-testid="soc-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Green Meadows CHS Ltd" /></Field>
                <Field label="Registration Number"><Input data-testid="soc-reg" value={form.registration_number || ""} onChange={(e) => set("registration_number", e.target.value)} placeholder="PLG/BSR/2020/1234" /></Field>
                <Field label="Address"><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="Street, Area" /></Field>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-4">
                <h2 className="font-display text-xl font-bold">Location</h2>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="City"><Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} placeholder="Boisar" /></Field>
                  <Field label="State"><Input value={form.state || ""} onChange={(e) => set("state", e.target.value)} /></Field>
                  <Field label="PIN Code"><Input value={form.pincode || ""} onChange={(e) => set("pincode", e.target.value)} placeholder="401501" /></Field>
                  <Field label="Financial Year"><Input value={form.financial_year || "2025-2026"} onChange={(e) => set("financial_year", e.target.value)} /></Field>
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-4">
                <h2 className="font-display text-xl font-bold">Structure & Committee</h2>
                <div className="grid grid-cols-3 gap-4">
                  <Field label="Buildings"><Input type="number" value={form.buildings || 1} onChange={(e) => set("buildings", +e.target.value)} /></Field>
                  <Field label="Wings"><Input value={form.wings || ""} onChange={(e) => set("wings", e.target.value)} placeholder="A, B" /></Field>
                  <Field label="Total Flats"><Input type="number" value={form.flats || ""} onChange={(e) => set("flats", +e.target.value)} /></Field>
                </div>
                <Field label="Secretary Name"><Input value={form.secretary_name || ""} onChange={(e) => set("secretary_name", e.target.value)} /></Field>
                <Field label="Secretary Mobile"><Input value={form.secretary_mobile || ""} onChange={(e) => set("secretary_mobile", e.target.value)} /></Field>
              </div>
            )}
            {step === 4 && (
              <div className="space-y-4">
                <h2 className="font-display text-xl font-bold">Services of Interest</h2>
                <p className="text-sm text-muted-foreground">Select the services your society may need (optional).</p>
                <div className="flex flex-wrap gap-2">
                  {SERVICES.map((s) => (
                    <button key={s} type="button" onClick={() => toggle("services_interested", s)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.services_interested.includes(s) ? "bg-primary text-white border-primary" : "bg-background hover:bg-muted"}`}>
                      {s}
                    </button>
                  ))}
                </div>
                <OtpVerify form={form} set={set} user={user} otpOk={otpOk} setOtpOk={setOtpOk} />
              </div>
            )}
            <div className="flex justify-between mt-8">
              <Button variant="ghost" onClick={() => (step === 1 ? setPath(null) : setStep(step - 1))} className="gap-2 rounded-full"><ArrowLeft className="w-4 h-4" /> Back</Button>
              {step < 4 ? (
                <Button data-testid="soc-next" onClick={() => setStep(step + 1)} className="gap-2 rounded-full">Next <ArrowRight className="w-4 h-4" /></Button>
              ) : (
                <Button data-testid="soc-finish" onClick={submitSociety} disabled={saving} className="gap-2 rounded-full">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Create Workspace
                </Button>
              )}
            </div>
          </div>
        )}

        {path === "vendor" && (
          <div className="bg-card border rounded-xl p-6 md:p-8 space-y-4">
            <h2 className="font-display text-xl font-bold">Vendor / Technician Registration</h2>
            <div className="flex gap-3" data-testid="ven-applicant-type">
              {[["VENDOR", "Business / Company"], ["TECHNICIAN", "Individual Technician"]].map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => set("applicant_type", val)}
                  data-testid={`ven-applicant-type-${val}`}
                  className={`px-4 py-2 rounded-full text-sm border ${(form.applicant_type || "VENDOR") === val ? "bg-secondary text-white border-secondary" : "bg-background hover:bg-muted"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <Field label="Business / Full Name *"><Input data-testid="ven-name" value={form.business_name || ""} onChange={(e) => set("business_name", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Contact Person"><Input value={form.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} /></Field>
              <Field label="Mobile"><Input value={form.mobile || ""} onChange={(e) => set("mobile", e.target.value)} /></Field>
              <Field label="City"><Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} /></Field>
              <Field label="GSTIN (if registered)"><Input data-testid="ven-gstin" value={form.gst || ""} onChange={(e) => set("gst", e.target.value.toUpperCase())} /></Field>
            </div>
            <div className="border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="ven-kyc-section">
              <div>
                <h3 className="font-display font-bold">KYC and business verification</h3>
                <p className="mt-1 text-xs text-slate-600">Provide reference details now, then upload the supporting records after submitting your application.</p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="PAN"><Input data-testid="ven-pan" value={form.pan || ""} onChange={(e) => set("pan", e.target.value.toUpperCase())} /></Field>
                <Field label="Business registration / Udyam no."><Input data-testid="ven-registration-number" value={form.business_registration_number || ""} onChange={(e) => set("business_registration_number", e.target.value)} /></Field>
              </div>
              <label className="flex items-start gap-2 text-sm" data-testid="ven-kyc-consent-label">
                <input checked={Boolean(form.kyc_consent)} data-testid="ven-kyc-consent" onChange={(e) => set("kyc_consent", e.target.checked)} type="checkbox" />
                <span>I authorize MySocietyMitra to use these records only for vendor verification and service operations.</span>
              </label>
            </div>
            {form.applicant_type === "TECHNICIAN" && (
              <div className="grid grid-cols-2 gap-4" data-testid="ven-technician-fields">
                <Field label="Trade licence number"><Input value={form.license_number || ""} onChange={(e) => set("license_number", e.target.value)} data-testid="ven-licence" /></Field>
                <Field label="Insurance provider"><Input value={form.insurance_provider || ""} onChange={(e) => set("insurance_provider", e.target.value)} data-testid="ven-insurance" /></Field>
                <Field label="Insurance expiry (optional)"><Input type="date" value={form.insurance_expiry || ""} onChange={(e) => set("insurance_expiry", e.target.value)} /></Field>
                <Field label="Skills (comma separated)"><Input value={(form.skills || []).join(", ")} onChange={(e) => set("skills", e.target.value.split(",").map((v) => v.trim()).filter(Boolean))} data-testid="ven-skills" /></Field>
              </div>
            )}
            {form.applicant_type === "TECHNICIAN" && pilotMatch && (
              <div className="border-l-4 border-emerald-500 bg-emerald-50 p-3 space-y-2 text-sm" data-testid="ven-pilot-match">
                <p>
                  <strong>Invite-only pilot detected:</strong> {pilotMatch.name} ({pilotMatch.region.city}, {pilotMatch.region.district}, {pilotMatch.region.state})
                  — {pilotMatch.cap.remaining}/{pilotMatch.cap.max} slot(s) remaining.
                </p>
                <p className="text-xs text-emerald-800">{pilotMatch.watermark}</p>
                <div className="flex items-center gap-2">
                  <Input
                    data-testid="ven-pilot-invite"
                    placeholder="Pilot invite code (e.g. RT-ABCDEF-123456)"
                    value={form.pilot_invite_code || ""}
                    onChange={(e) => set("pilot_invite_code", e.target.value.toUpperCase())}
                    onBlur={checkInvite}
                  />
                  {inviteCheck.status === "ok" && <span className="text-emerald-700 text-xs">✓ valid</span>}
                  {inviteCheck.status === "bad" && <span className="text-red-700 text-xs">invalid / consumed / revoked</span>}
                </div>
              </div>
            )}
            <div>
              <Label className="mb-2 block">Service Categories</Label>
              <div className="flex flex-wrap gap-2">
                {SERVICES.map((s) => (
                  <button key={s} type="button" onClick={() => toggle("categories", s)}
                    className={`px-3 py-1.5 rounded-full text-sm border ${form.categories.includes(s) ? "bg-secondary text-white border-secondary" : "bg-background hover:bg-muted"}`}>{s}</button>
                ))}
              </div>
            </div>
            <OtpVerify form={form} set={set} user={user} otpOk={otpOk} setOtpOk={setOtpOk} />

            {/* Agreement acceptance step — required server-side */}
            <div className="border-t pt-4 mt-4" data-testid="ven-agreement-section">
              <h3 className="font-display font-bold">Service Agreement &amp; Commercial Schedule</h3>
              {!currentLegal ? (
                <p className="text-sm text-muted-foreground mt-2">Loading current agreement…</p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mt-1">
                    Version {currentLegal.agreement?.version} ({currentLegal.agreement?.status}){" "}
                    · Schedule v{currentLegal.schedule?.version} ({currentLegal.schedule?.status})
                    {currentLegal.agreement?.watermark && (
                      <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs">{currentLegal.agreement.watermark}</span>
                    )}
                  </p>
                  <div className="max-h-56 overflow-y-auto border p-3 text-xs whitespace-pre-wrap font-mono bg-slate-50 mt-2" data-testid="ven-agreement-text">
                    {currentLegal.agreement?.text_md}
                  </div>
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs border" data-testid="ven-schedule-table">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="p-2 text-left">Category</th>
                          <th className="p-2 text-left">Commission</th>
                          <th className="p-2 text-left">Visiting fee</th>
                          <th className="p-2 text-left">Cancellation</th>
                          <th className="p-2 text-left">Warranty</th>
                          <th className="p-2 text-left">Weekly payout</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(currentLegal.schedule?.rows || []).map((row, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-2">{row.category_name}</td>
                            <td className="p-2">{row.commission_percent}%</td>
                            <td className="p-2">{row.visiting_fee}</td>
                            <td className="p-2">{row.cancellation_rules}</td>
                            <td className="p-2">{row.warranty_revisit}</td>
                            <td className="p-2">{row.weekly_payout_rule}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <label className="flex items-start gap-2 text-sm mt-3">
                    <input type="checkbox" checked={ackTerms} onChange={(e) => setAckTerms(e.target.checked)} data-testid="ven-ack-terms" />
                    <span>I have read and accept the Vendor/Technician Service Agreement and the displayed Commercial Schedule, including commission, visiting-fee, cancellation and payout rules.</span>
                  </label>
                  <label className="flex items-start gap-2 text-sm mt-2">
                    <input type="checkbox" checked={ackPrivacy} onChange={(e) => setAckPrivacy(e.target.checked)} data-testid="ven-ack-privacy" />
                    <span>I acknowledge the Privacy Notice and authorized use of my information for verification, assignments and payouts.</span>
                  </label>
                </>
              )}
            </div>

            <div className="flex justify-between mt-4">
              <Button variant="ghost" onClick={() => setPath(null)} className="gap-2 rounded-full"><ArrowLeft className="w-4 h-4" /> Back</Button>
              <Button data-testid="ven-finish" onClick={submitVendor} disabled={saving || !(ackTerms && ackPrivacy)} className="gap-2 rounded-full">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Register
              </Button>
            </div>
          </div>
        )}

        {path === "expert" && (
          <div className="bg-card border rounded-xl p-6 md:p-8 space-y-4">
            <h2 className="font-display text-xl font-bold">Expert Application</h2>
            <p className="text-sm text-muted-foreground">Your profile will be verified by MySocietyMitra before it goes live to societies.</p>
            <Field label="Full Name *"><Input data-testid="exp-name" value={form.name || user.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Mobile"><Input value={form.mobile || ""} onChange={(e) => set("mobile", e.target.value)} /></Field>
              <Field label="Experience"><Input value={form.experience || ""} onChange={(e) => set("experience", e.target.value)} placeholder="e.g. 10 years" /></Field>
              <Field label="Qualification"><Input value={form.qualification || ""} onChange={(e) => set("qualification", e.target.value)} placeholder="e.g. LLB, CA" /></Field>
              <Field label="Service Area / Cities"><Input value={form.service_area || ""} onChange={(e) => set("service_area", e.target.value)} placeholder="Palghar, Thane" /></Field>
              <Field label="Languages"><Input value={form.languages || ""} onChange={(e) => set("languages", e.target.value)} placeholder="English, Hindi, Marathi" /></Field>
              <Field label="Your Rate ₹/min (optional)"><Input type="number" data-testid="exp-rate" value={form.rate_per_min_override || ""} onChange={(e) => set("rate_per_min_override", e.target.value)} placeholder="Platform default" /></Field>
            </div>
            <div>
              <Label className="mb-2 block">Specializations *</Label>
              <div className="flex flex-wrap gap-2">
                {EXPERT_FIELDS.map((s) => (
                  <button key={s} type="button" data-testid={`exp-cat-${s}`} onClick={() => toggle("categories", s)}
                    className={`px-3 py-1.5 rounded-full text-sm border ${form.categories.includes(s) ? "bg-amber-500 text-white border-amber-500" : "bg-background hover:bg-muted"}`}>{s}</button>
                ))}
              </div>
            </div>
            <Field label="Short Bio"><Input value={form.bio || ""} onChange={(e) => set("bio", e.target.value)} placeholder="Tell societies about your expertise" /></Field>
            <OtpVerify form={form} set={set} user={user} otpOk={otpOk} setOtpOk={setOtpOk} />
            <div className="flex justify-between mt-4">
              <Button variant="ghost" onClick={() => setPath(null)} className="gap-2 rounded-full"><ArrowLeft className="w-4 h-4" /> Back</Button>
              <Button data-testid="exp-finish" onClick={submitExpert} disabled={saving} className="gap-2 rounded-full">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Submit Application
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="mb-1.5 block text-sm">{label}</Label>{children}</div>;
}

function OtpVerify({ form, set, user, otpOk, setOtpOk }) {
  const [sent, setSent] = useState({});
  const [code, setCode] = useState({});
  const send = async (channel) => {
    const target = channel === "mobile" ? form.mobile : (form.email || user.email);
    if (!target) { toast.error(`Enter your ${channel} first`); return; }
    try {
      const r = await api.post("/otp/send", { channel, target });
      setSent((s) => ({ ...s, [channel]: true }));
      toast.success(r.data.dev_otp ? `Code (preview): ${r.data.dev_otp}` : "Code sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send code"); }
  };
  const verify = async (channel) => {
    const target = channel === "mobile" ? form.mobile : (form.email || user.email);
    try {
      await api.post("/otp/verify", { channel, target, code: code[channel] });
      setOtpOk((o) => ({ ...o, [channel]: true }));
      toast.success(`${channel} verified`);
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
  };
  return (
    <div className="border rounded-xl p-4 space-y-3 bg-muted/30">
      <div className="text-sm font-semibold">Verify your email (required)</div>
      {["email"].map((ch) => (
        <div key={ch} className="flex flex-wrap items-center gap-2" data-testid={`otp-row-${ch}`}>
          <Input className="max-w-[220px]" placeholder={user.email || "Email"}
            value={form.email ?? user.email ?? ""}
            data-testid={`otp-${ch}-input`} disabled />
          {otpOk[ch] ? <span className="text-xs text-emerald-600 font-medium">✓ Verified</span> : sent[ch] ? (
            <>
              <Input className="max-w-[110px]" placeholder="6-digit code" value={code[ch] || ""} onChange={(e) => setCode((c) => ({ ...c, [ch]: e.target.value }))} data-testid={`otp-${ch}-code`} />
              <Button type="button" size="sm" className="rounded-full" onClick={() => verify(ch)} data-testid={`otp-${ch}-verify`}>Verify</Button>
            </>
          ) : <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => send(ch)} data-testid={`otp-${ch}-send`}>Send OTP</Button>}
        </div>
      ))}
    </div>
  );
}
